import { z } from "zod";
import { parse, isAfter, isValid } from "date-fns";

const dateFormat = "dd/MM/yyyy";

const parseDate = (value?: string) => {
  if (!value) return null;
  const parsed = parse(value, dateFormat, new Date());
  return isValid(parsed) ? parsed : null;
};

const establishmentSchema = z.object({
  id: z.string(),
  display_name: z.string(),
});

const conditionSchema = z.object({
  id: z.string(),
  condition_group_label: z.string(),
  type_label: z.string(),
  value_label: z.string(),
  provider_klass: z.string().optional(),
  properties: z.record(z.any()).optional(),
});

const rewardSchema = z
  .object({
    id: z.string().optional(),
    mission_reward_id: z.string().optional().nullable(),
    pivot_id: z.string().optional().nullable(),
    reward_type: z.enum(["product", "points", "tickets"]).default("product"),
    quantity: z.coerce.number().optional().default(1),
    reward_id: z.string().optional().nullable(),
    reward: z.object({
      id: z.string(),
      display_name: z.string()
    }).optional(),
    product_id: z.string().optional().nullable(),
    reward_label: z.string().optional().nullable(),
    product_name: z.string().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    const requiresProduct = data.reward_type === "product";
    const hasProductReference =
      data.product_id || data.reward_id || data.reward_label;
    if (requiresProduct && !hasProductReference) {
      ctx.addIssue({
        path: ["reward_id"],
        code: z.ZodIssueCode.custom,
        message: "Selecione um produto",
      });
    }

    if (!data.quantity || Number(data.quantity) < 1) {
      ctx.addIssue({
        path: ["quantity"],
        code: z.ZodIssueCode.custom,
        message: "Qtd. obrigatória",
      });
    }
  });

export const missionSchema = z
  .object({
    mission_type: z.enum(["tcard", "establishment"]),
    establishment: establishmentSchema.nullable().optional(),
    status: z.enum(["active", "disabled"]),
    name: z.string().min(1, { message: "Campo obrigatório" }),
    description: z.string().min(1, { message: "Campo obrigatório" }),
    allow_multiple_times: z.boolean().default(false),
    max_times: z.number().nullable().optional(),
    minimum_loyalty: z.enum([
      "indifferent",
      "bronze",
      "silver",
      "gold",
      "platinum",
      "diamond",
    ]),
    start_date: z.string().min(1, { message: "Campo obrigatório" }),
    end_date: z.string().min(1, { message: "Campo obrigatório" }),
    restrict_establishments: z.boolean().default(false),
    allowed_establishments: z.array(establishmentSchema).default([]),
    conditions: z.array(conditionSchema).default([]),
    rewards: z.array(rewardSchema).default([]),
  })
  .superRefine((data, ctx) => {
    const start = parseDate(data.start_date);
    const end = parseDate(data.end_date);

    if (!start) {
      ctx.addIssue({
        path: ["start_date"],
        code: z.ZodIssueCode.custom,
        message: "Data inválida",
      });
    }

    if (!end) {
      ctx.addIssue({
        path: ["end_date"],
        code: z.ZodIssueCode.custom,
        message: "Data inválida",
      });
    }

    if (start && end && !isAfter(end, start)) {
      ctx.addIssue({
        path: ["end_date"],
        code: z.ZodIssueCode.custom,
        message: "Fim deve ser após o inicio",
      });
    }

    if (data.allow_multiple_times && (!data.max_times || data.max_times <= 0)) {
      ctx.addIssue({
        path: ["max_times"],
        code: z.ZodIssueCode.custom,
        message: "Informe o máximo de vezes",
      });
    }

    if (data.mission_type === "establishment" && !data.establishment) {
      ctx.addIssue({
        path: ["establishment"],
        code: z.ZodIssueCode.custom,
        message: "Selecione o estabelecimento",
      });
    }

    if (
      data.restrict_establishments &&
      data.allowed_establishments.length === 0
    ) {
      ctx.addIssue({
        path: ["allowed_establishments"],
        code: z.ZodIssueCode.custom,
        message: "Selecione ao menos um estabelecimento",
      });
    }

    if (!data.conditions || data.conditions.length === 0) {
      ctx.addIssue({
        path: ["conditions"],
        code: z.ZodIssueCode.custom,
        message: "Adicione ao menos um requisito",
      });
    }

  });

export type MissionSchema = z.infer<typeof missionSchema>;
